import React, { createContext, useContext, useState, ReactNode } from 'react'

type TriggerType = 'delay' | 'scroll' | 'manual' | 'unknown'
type PageType = 'home' | 'public_restaurant' | 'unknown'

interface PromoModalContextType {
  isOpen: boolean
  isMobile: boolean
  lastTrigger: TriggerType
  lastPage: PageType
  openModal: (isMobile?: boolean) => void
  closeModal: () => void
  setLastPromoTrigger: (trigger: TriggerType, page?: PageType) => void
}

const PromoModalContext = createContext<PromoModalContextType | undefined>(undefined)

export const PromoModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [lastTrigger, setLastTrigger] = useState<TriggerType>('unknown')
  const [lastPage, setLastPage] = useState<PageType>('unknown')

  const openModal = (mobile = false): void => {
    setIsMobile(mobile)
    setIsOpen(true)
  }

  const closeModal = (): void => {
    setIsOpen(false)
    setIsMobile(false)
  }

  const setLastPromoTrigger = (trigger: TriggerType, page?: PageType): void => {
    setLastTrigger(trigger)
    if (page) {
      setLastPage(page)
    }
    // Also store in sessionStorage as backup
    sessionStorage.setItem('promo_last_trigger', trigger)
    if (page) {
      sessionStorage.setItem('promo_last_page', page)
    }
  }

  return (
    <PromoModalContext.Provider
      value={{
        isOpen,
        isMobile,
        lastTrigger,
        lastPage,
        openModal,
        closeModal,
        setLastPromoTrigger,
      }}
    >
      {children}
    </PromoModalContext.Provider>
  )
}

export const usePromoModal = (): PromoModalContextType => {
  const context = useContext(PromoModalContext)
  if (!context) {
    throw new Error('usePromoModal must be used within PromoModalProvider')
  }
  return context
}
